import { Immutable } from "immer";
import React from "react";

import type { LandingProductInspirationBlock as Value } from "@reactivated";

// NOTE: This component reproduces the legacy server-rendered markup from
// `cms/landing_page.html` (the `block_type == 'product_inspiration_block'`
// branch) verbatim — every classname is load-bearing for the existing SCSS, so
// keep them in sync.
//
// LANDING vs HOME differences (the HOME variant lives in
// `../ProductInspirationBlock`):
//   * the root uses `home-page-inspiration landing-page-inspiration` (HOME used
//     `home-page-inspiration home-page-section`);
//   * there is NO `<hr class="home-page-inspiration__top-border">` here;
//   * the h2 carries the `home-page-inspiration__title--landing-page
//     landing-page__title` modifiers;
//   * the tile-list carries `landing-page-inspiration__tile-list
//     landing-page__section` modifiers.
//
// Field handling: `title` (block-level) and each tile's
// `title`/`description`/`link_name` are plain CharBlocks → render as escaped
// text children to match Django autoescape. The `items-length-N` modifier uses
// N = total tile count (legacy `block.value.tile|length`), so it is constant
// across every tile.
type IProps = Immutable<{
    value: Value;
}>;

type Tile = NonNullable<Value["tile"]>[number];

const backgroundImageStyle = (
    image: Tile["image"],
): React.CSSProperties | undefined => {
    // Legacy emitted `style="background-image: url(<url>);"` (no quotes). When
    // the image is null we omit the inline style entirely while keeping the
    // wrapper element. See the HOME ProductInspirationBlock precedent.
    if (!image) {
        return undefined;
    }
    return { backgroundImage: `url(${image.url})` };
};

export const LandingProductInspirationBlock = (
    props: IProps,
): React.JSX.Element => {
    const v = props.value;
    const tiles = v.tile ?? [];

    return (
        <div className="l-full-width home-page-inspiration landing-page-inspiration">
            <h2 className="home-page-inspiration__title home-page-inspiration__title--landing-page landing-page__title">
                {v.title ?? ""}
            </h2>
            <div className="home-page-inspiration__tile-list landing-page-inspiration__tile-list landing-page__section">
                {tiles.map((tile, index) => (
                    <div
                        key={index}
                        className={`home-page-inspiration__tile-item items-length-${tiles.length}`}
                        style={backgroundImageStyle(tile.image)}
                    >
                        <div className="home-page-inspiration__tile-item__cover"></div>
                        <a
                            className="home-page-inspiration__tile-item__cover home-page-inspiration__tile-item__cover--link"
                            title={tile.title ?? ""}
                            href={tile.link?.url ?? ""}
                        >
                            <div className="home-page-inspiration__container">
                                <div className="home-page-inspiration__tile-item__header">
                                    <div className="home-page-inspiration__tile-item__header-content">
                                        <span className="home-page-inspiration__tile-item__header-cont">
                                            {tile.title ?? ""}
                                        </span>
                                        <span className="home-page-inspiration__tile-item__header--desc">
                                            {tile.description ?? ""}
                                        </span>
                                    </div>
                                </div>
                                <div className="home-page-inspiration__tile-item__footer">
                                    <span className="home-page-inspiration__tile-item__link">
                                        {tile.link_name ?? ""}
                                    </span>
                                </div>
                            </div>
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
};
